import { endOfDay, startOfDay } from "date-fns";
import { INTEGRATION_DOCUMENT_SOURCES } from "@common/constants/financial-documents";
import { FiltersTypes } from "@common/types/filters/general";
import {
    AccrualDraftCounterpartyState,
    FinancialDocumentExtended,
} from "@common/types/financialDocument";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import { getAccrualLineItemExtraction } from "./accrualImporter.helpers";
import { AccrualImporterDocumentRow } from "./accrualImporter.types";

const INTEGRATION_SOURCES = new Set<string>(INTEGRATION_DOCUMENT_SOURCES);

export function accrualDraftSourceKey(source: string | undefined): string {
    if (source && INTEGRATION_SOURCES.has(source)) {
        return source;
    }
    if (source?.includes("@")) {
        return "email";
    }
    return "upload";
}

function counterpartyState(extraction: {
    counterpartyId: string | null;
    counterpartyName: string | null;
}): AccrualDraftCounterpartyState {
    if (extraction.counterpartyId) {
        return AccrualDraftCounterpartyState.MATCHED;
    }
    if (extraction.counterpartyName?.trim()) {
        return AccrualDraftCounterpartyState.NEEDS_CONFIRMATION;
    }
    return AccrualDraftCounterpartyState.EMPTY;
}

function matchesDocumentFilters({
    financialDocument,
    filters,
    mode,
}: {
    financialDocument: FinancialDocumentExtended;
    filters: FiltersTypes;
    mode: AccrualDocumentImporterMode;
}): boolean {
    const createdAt = new Date(financialDocument.createdAt);
    if (filters.start && createdAt < startOfDay(filters.start)) {
        return false;
    }
    if (filters.end && createdAt > endOfDay(filters.end)) {
        return false;
    }

    const statuses = filters.accrualReviewStatuses ?? [];
    const status = financialDocument.accrualReviewStatus;
    if (statuses.length > 0 && (!status || !statuses.includes(status))) {
        return false;
    }

    const extraction = getAccrualLineItemExtraction(financialDocument, mode);
    const documentDate = extraction?.documentDate ?? null;
    if (
        filters.invoiceDateFrom &&
        (!documentDate || documentDate < filters.invoiceDateFrom)
    ) {
        return false;
    }
    if (
        filters.invoiceDateTo &&
        (!documentDate || documentDate > filters.invoiceDateTo)
    ) {
        return false;
    }

    if (
        typeof filters.hasDueDate === "boolean" &&
        Boolean(extraction?.dueDate) !== filters.hasDueDate
    ) {
        return false;
    }

    const counterpartyStates = filters.draftCounterpartyStates ?? [];
    return (
        counterpartyStates.length === 0 ||
        counterpartyStates.includes(
            counterpartyState({
                counterpartyId: extraction?.counterpartyId ?? null,
                counterpartyName: extraction?.counterpartyName ?? null,
            }),
        )
    );
}

function hasDocumentFilters(filters: FiltersTypes): boolean {
    return [
        filters.start !== undefined,
        filters.end !== undefined,
        Boolean(filters.invoiceDateFrom),
        Boolean(filters.invoiceDateTo),
        typeof filters.hasDueDate === "boolean",
        (filters.accrualReviewStatuses?.length ?? 0) > 0,
        (filters.draftCounterpartyStates?.length ?? 0) > 0,
    ].some(Boolean);
}

export function filterAccrualImporterDraftRows({
    rows,
    filters,
    search,
    mode,
}: {
    rows: AccrualImporterDocumentRow[];
    filters: FiltersTypes;
    search?: string;
    mode: AccrualDocumentImporterMode;
}): AccrualImporterDocumentRow[] {
    const query = search?.trim().toLowerCase();
    const entityIds = filters.entityIds ?? [];
    const sources = filters.draftSources ?? [];
    const shouldMatchDocument = hasDocumentFilters(filters);

    return rows.filter((row) => {
        const { financialDocument } = row;
        const entityId = financialDocument?.entityId;
        if (
            entityIds.length > 0 &&
            entityId != null &&
            !entityIds.includes(entityId)
        ) {
            return false;
        }
        if (
            sources.length > 0 &&
            !sources.includes(accrualDraftSourceKey(financialDocument?.source))
        ) {
            return false;
        }
        if (shouldMatchDocument) {
            if (
                !financialDocument ||
                !matchesDocumentFilters({ financialDocument, filters, mode })
            ) {
                return false;
            }
        }
        if (!query) {
            return true;
        }
        const fileName = financialDocument?.fileName ?? row.file?.name;
        return fileName?.toLowerCase().includes(query) ?? false;
    });
}
